"use client";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { Home, Info, Phone, Search, X } from "lucide-react";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { ThemeToggle } from "@/components/system";
import { debounce } from "@/lib/utils/debounce";

import styles from "./Header.module.css";

type PageResult = {
  id: string;
  title: string;
  slug: string;
};

type Props = {
  initialTheme: "dark" | "light";
};

type CacheEntry = {
  data: PageResult[];
  ts: number;
};

const CACHE_TTL = 5 * 60 * 1000;
const queryCache = new Map<string, CacheEntry>();

function cacheGet(q: string) {
  const entry = queryCache.get(q);
  if (!entry) return null;

  if (Date.now() - entry.ts > CACHE_TTL) {
    queryCache.delete(q);
    return null;
  }

  return entry.data;
}

function cacheSet(q: string, data: PageResult[]) {
  queryCache.set(q, { data, ts: Date.now() });
}

function buildHref(result: PageResult) {
  return `/${result.slug}`;
}

function useIsTabletUp() {
  const [isTabletUp, setIsTabletUp] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsTabletUp(media.matches);

    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isTabletUp;
}

export default function HeaderInteractive({ initialTheme }: Props) {
  const pathname = usePathname();
  const isTabletUp = useIsTabletUp();
  const desktopListboxId = useId();
  const mobileListboxId = useId();

  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState<PageResult[]>([]);
  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const mobileInputRef = useRef<HTMLInputElement | null>(null);
  const desktopInputRef = useRef<HTMLInputElement | null>(null);
  const desktopSearchShellRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchShellRef = useRef<HTMLDivElement | null>(null);

  const trimmedQuery = searchValue.trim();

  const closeSearch = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSearchOpen(false);
    setResults([]);
    setIsSearching(false);
  }, []);

  const resetSearch = useCallback(() => {
    setSearchValue("");
    closeSearch();
  }, [closeSearch]);

  const closeMobileSearch = useCallback(() => {
    setMobileSearchActive(false);
    resetSearch();
  }, [resetSearch]);

  const openMobileSearch = useCallback(() => {
    setMobileSearchActive(true);
  }, []);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (rawQuery: string) => {
        const q = rawQuery.trim();

        if (q.length < 2) {
          setResults([]);
          setSearchOpen(false);
          setIsSearching(false);
          return;
        }

        const cached = cacheGet(q);
        if (cached) {
          setResults(cached);
          setSearchOpen(true);
          setIsSearching(false);
          return;
        }

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsSearching(true);

        try {
          const res = await fetch(`/api/search/levels?q=${encodeURIComponent(q)}`, {
            signal: controller.signal,
          });

          if (!res.ok) {
            setIsSearching(false);
            return;
          }

          const json = await res.json();
          const data: PageResult[] = Array.isArray(json?.data) ? json.data : [];

          setResults(data);
          setSearchOpen(true);
          cacheSet(q, data);
        } catch {
          // ignore abort/network errors
        } finally {
          if (abortRef.current === controller) {
            setIsSearching(false);
          }
        }
      }, 250),
    []
  );

  useEffect(() => {
    debouncedFetch(searchValue);

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchValue, debouncedFetch]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  useEffect(() => {
    if (mobileSearchActive && !isTabletUp) {
      requestAnimationFrame(() => mobileInputRef.current?.focus());
    }
  }, [mobileSearchActive, isTabletUp]);

  useEffect(() => {
    setMobileSearchActive(false);
    resetSearch();
  }, [pathname, resetSearch]);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>("[data-site-header]");
    if (!header) return;

    header.setAttribute("data-search-active", mobileSearchActive ? "true" : "false");

    return () => {
      header.setAttribute("data-search-active", "false");
    };
  }, [mobileSearchActive]);

  useEffect(() => {
    if (isTabletUp && mobileSearchActive) {
      setMobileSearchActive(false);
    }
  }, [isTabletUp, mobileSearchActive]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (mobileSearchActive) {
          closeMobileSearch();
        } else {
          closeSearch();
          desktopInputRef.current?.blur();
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileSearchActive, closeMobileSearch, closeSearch]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDesktop = desktopSearchShellRef.current?.contains(target) ?? false;
      const insideMobile = mobileSearchShellRef.current?.contains(target) ?? false;

      if (!insideDesktop && !insideMobile) {
        setSearchOpen(false);

        if (!isTabletUp && mobileSearchActive) {
          closeMobileSearch();
        }
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isTabletUp, mobileSearchActive, closeMobileSearch]);

  const showSearchResults =
    searchOpen && trimmedQuery.length >= 2 && (results.length > 0 || isSearching);

  return (
    <>
      <div ref={desktopSearchShellRef} className={styles.desktopSearch}>
        <label htmlFor="desktop-site-search" className="sr-only">
          Search base pages
        </label>

        <input
          id="desktop-site-search"
          ref={desktopInputRef}
          type="search"
          inputMode="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search Town Hall…"
          value={searchValue}
          onFocus={() => {
            if (trimmedQuery.length >= 2) setSearchOpen(true);
          }}
          onChange={(e) => setSearchValue(e.target.value)}
          className={styles.desktopSearchInput}
          aria-controls={desktopListboxId}
          aria-autocomplete="list"
        />

        {showSearchResults && (
          <div
            id={desktopListboxId}
            className={styles.searchDropdown}
            role="listbox"
            aria-label="Search results"
          >
            {isSearching && results.length === 0 ? (
              <div className={styles.searchItem} aria-live="polite">
                Searching…
              </div>
            ) : results.length > 0 ? (
              results.map((result) => (
                <Link
                  key={result.id}
                  href={buildHref(result)}
                  className={styles.searchItem}
                  role="option"
                  onClick={resetSearch}
                >
                  {result.title}
                </Link>
              ))
            ) : (
              <div className={styles.searchItem} aria-live="polite">
                No results found.
              </div>
            )}
          </div>
        )}
      </div>

      <div
        ref={mobileSearchShellRef}
        className={`${styles.mobileInlineSearch} ${
          mobileSearchActive ? styles.visible : ""
        }`}
      >
        <Search size={18} aria-hidden="true" />

        <label htmlFor="mobile-site-search" className="sr-only">
          Search base pages
        </label>

        <input
          id="mobile-site-search"
          ref={mobileInputRef}
          type="search"
          inputMode="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search Town Hall…"
          value={searchValue}
          onFocus={() => {
            if (trimmedQuery.length >= 2) setSearchOpen(true);
          }}
          onChange={(e) => setSearchValue(e.target.value)}
          aria-controls={mobileListboxId}
          aria-autocomplete="list"
        />

        {showSearchResults && (
          <div
            id={mobileListboxId}
            className={styles.searchDropdown}
            role="listbox"
            aria-label="Search results"
          >
            {isSearching && results.length === 0 ? (
              <div className={styles.searchItem} aria-live="polite">
                Searching…
              </div>
            ) : results.length > 0 ? (
              results.map((result) => (
                <Link
                  key={result.id}
                  href={buildHref(result)}
                  className={styles.searchItem}
                  role="option"
                  onClick={closeMobileSearch}
                >
                  {result.title}
                </Link>
              ))
            ) : (
              <div className={styles.searchItem} aria-live="polite">
                No results found.
              </div>
            )}
          </div>
        )}
      </div>

      <nav className={styles.nav} aria-label="Primary">
        <div className={styles.tabletLinks}>
          <Link href="/" className={styles.iconLink} aria-label="Home">
            <Home size={20} aria-hidden="true" />
          </Link>

          <Link
            href="/legal/about"
            className={styles.iconLink}
            aria-label="About"
          >
            <Info size={20} aria-hidden="true" />
          </Link>

          <Link
            href="/legal/contact"
            className={styles.iconLink}
            aria-label="Contact"
          >
            <Phone size={20} aria-hidden="true" />
          </Link>
        </div>

        <button
          type="button"
          className={styles.iconBtn}
          aria-label={mobileSearchActive ? "Close search" : "Open search"}
          aria-expanded={mobileSearchActive}
          onClick={() => {
            if (mobileSearchActive) {
              closeMobileSearch();
            } else {
              openMobileSearch();
            }
          }}
        >
          {mobileSearchActive ? (
            <X size={20} aria-hidden="true" />
          ) : (
            <Search size={20} aria-hidden="true" />
          )}
        </button>

        <ThemeToggle initialTheme={initialTheme} />
      </nav>

      {mobileSearchActive && !isTabletUp && (
        <div
          className={styles.searchOverlay}
          onClick={closeMobileSearch}
          aria-hidden="true"
        />
      )}
    </>
  );
}